import {
  FormBuilder, FormGroup, Validators,
} from '@angular/forms';

import { InventoryActionProduct, toInventoryActionProductForm } from './inventory-action-product';
import { Warehouse } from './warehouse.model';
import { Invoice } from './invoice.model';
import { Folder } from './folder.model';

export interface InventoryAction {
  id: number;
  approval: boolean;
  approved: boolean;
  buy_order?: InventoryAction;
  change_log: Partial<InventoryAction>[];
  comment: string;
  company_id: number;
  completed: boolean;
  completed_at: Date;
  correlative_id: number;
  created_at: Date;
  custom_fields: Record<string, unknown>;
  extra_params: InventoryActionExtraParams;
  document_id: number | null;
  document_type: string | null;
  document: any;
  draft: boolean;
  edited: boolean;
  estimated_completion_date: Date;
  expiration_date: Date;
  failed: boolean;
  finish: boolean;
  folder: Folder;
  folder_id: number;
  initial: boolean;
  inventory_action_products: InventoryActionProduct[];
  inventory_action_products_count: number;
  invoice?: Invoice;
  is_valid: boolean;
  last_edit_action: InventoryAction;
  locked: boolean;
  pictures: any[];
  pricings_children: InventoryAction[];
  normal: boolean;
  should_send_notification: boolean;
  status_id: number;
  status: any;
  stock?: InventoryAction;
  tag_id?: number;
  target_document_id: number | null;
  target_document_type: string | null;
  target_warehouse?: Warehouse;
  target_warehouse_id: number | null;
  total: number;
  type: string;
  updated_at: Date;
  owner: InventoryActionOwner;
  owner_id: number;
  warehouse?: Warehouse;
  warehouse_id: number;
  works: Record<string, unknown>[];
}

export interface InventoryActionForMovement extends InventoryAction {
  
}

export interface InventoryActionForStock extends InventoryAction {
  invoice?: Invoice,
}

interface InventoryActionOwner {
  id: number;
  name: string;
  email: string;
  tooltip?: string;
}

interface InventoryActionExtraParams {
  answered: boolean;
  answered_at: Date;
  required_delivery_date: Date;
  required_products: InventoryActionProduct[];
  selected_products: InventoryActionProduct[];
  provider_ids: number[];
  provider_names: string;
  provider_name: string;
}

export const allInventoryActionTypes = [
  'InventoryAction::Stock',
  'InventoryAction::Loss',
  'InventoryAction::Movement',
  'InventoryAction::Edit',
  'InventoryAction::Set',
  'InventoryAction::Consume',
  'InventoryAction::Return',
  'InventoryAction::Pricing',
  'InventoryAction::BuyOrder',
];

export const inventoryTaskTypes = [
  'InventoryAction::Stock',
  'InventoryAction::Loss',
  'InventoryAction::Movement',
  'InventoryAction::Edit',
  'InventoryAction::Set',
  'InventoryAction::Consume',
  'InventoryAction::Return',
]

export const providersTaskTypes = [
  'InventoryAction::Pricing',
  'InventoryAction::BuyOrder',
]

export function generateInventoryActionFormGroup({
  id = null,
  comment = null,
  document_type = null,
  document_id = null,
  draft = false,
  expiration_date = null,
  extra_params = {},
  type = 'InventoryAction::Consume',
  tag_id = null,
  warehouse_id = null,
  estimated_completion_date = null,
  inventory_action_products = [],
  pictures = [],
  document = {},
  status = {},
  owner_id = null,
  owner_type = null,
} = {}, {
  include_comments = false,
  include_expiration_date = false,
  include_document = false,
  include_tag = false,
  include_pictures = false,
  include_status = false,
  include_owner = false,
} = {}) : FormGroup {
  const fb = new FormBuilder();

  const group: FormGroup = fb.group({
    id: fb.control(id),
    draft: fb.control(draft),
    estimated_completion_date: fb.control(estimated_completion_date),
    type: fb.control(type, [Validators.required]),
    inventory_action_products: fb.array(
      inventory_action_products.map((product) => toInventoryActionProductForm(product)),
    ),
    extra_params: fb.group(Object.keys(extra_params).reduce((acc, key) => {
      acc[key] = fb.control(extra_params[key], {nonNullable: true});
      return acc;
    }, {})),
    warehouse_id: fb.control(warehouse_id, [Validators.required]),
  });

  if (include_expiration_date) {
    group.addControl('expiration_date', fb.control(expiration_date));
  }

  if (include_comments) {
    group.addControl('comment', fb.control(comment));
  }

  if (include_document) {
    group.addControl('document_type', fb.control(document_type, [Validators.required]));
    group.addControl('document_id', fb.control(document_id, [Validators.required]));
    group.addControl('document', fb.control(document));
  }

  if (include_tag) {
    group.addControl('tag_id', fb.control(tag_id, [Validators.required]));
  }

  if (include_pictures) {
    group.addControl('pictures', fb.array(
      pictures.map((picture) => fb.group(picture)),
    ));
  }

  if (include_status) {
    group.addControl('status', fb.control(status));
  }

  if (include_owner) {
    group.addControl('owner_id', fb.control(owner_id, [Validators.required]));
    group.addControl('owner_type', fb.control(owner_type, [Validators.required]));
  }
  return group;
}

export function calculateInventoryActionTotal(inventoryAction: InventoryAction) {
  return inventoryAction?.inventory_action_products?.reduce(
    (acc, product) => acc + product.amount * product.quantity,
    0,
  ) || 0;
}
